// Feature Flags Manager - Developer Tool
import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, RefreshCw, Plus, Trash2, Shield, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { featureFlagsApi } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';

export function FeatureFlagsManager() {
  const [flags, setFlags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showInitDialog, setShowInitDialog] = useState(false);

  const loadFlags = async () => {
    setIsLoading(true);
    try {
      const response = await featureFlagsApi.getAll();
      setFlags(response.data || []);
    } catch (error) {
      console.error('Failed to load feature flags:', error);
      toast.error('Fehler beim Laden der Feature Flags');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadFlags();
  }, []);

  const handleToggle = async (menuKey) => {
    try {
      await featureFlagsApi.toggle(menuKey);
      toast.success('Feature Flag aktualisiert');
      loadFlags();
    } catch (error) {
      console.error('Toggle failed:', error);
      toast.error('Fehler beim Umschalten');
    }
  };

  const handleInitialize = async () => {
    try {
      await featureFlagsApi.initialize();
      toast.success('Feature Flags initialisiert');
      setShowInitDialog(false);
      loadFlags();
    } catch (error) {
      console.error('Initialize failed:', error);
      toast.error('Fehler bei der Initialisierung');
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Menüpunkt-Verwaltung
              </CardTitle>
              <CardDescription className="mt-2">
                Steuern Sie, welche Menüpunkte für normale User sichtbar sind.
                Code bleibt im Hintergrund, aber Frontend zeigt ausgeblendete Features nicht an.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={loadFlags}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Neu laden
              </Button>
              {flags.length === 0 && (
                <Button size="sm" onClick={() => setShowInitDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Initialisieren
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {flags.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground mb-4">
                Keine Feature Flags gefunden. Initialisieren Sie die Standard-Flags.
              </p>
              <Button onClick={() => setShowInitDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Standard-Flags initialisieren
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Menüpunkt</TableHead>
                  <TableHead>Key</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Rollen</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {flags.map((flag) => (
                  <TableRow key={flag.id}>
                    <TableCell className="font-medium">{flag.label}</TableCell>
                    <TableCell>
                      <code className="text-xs bg-muted px-2 py-1 rounded">
                        {flag.menu_key}
                      </code>
                    </TableCell>
                    <TableCell>
                      {flag.ist_aktiv ? (
                        <Badge variant="success" className="flex items-center gap-1 w-fit">
                          <Eye className="w-3 h-3" />
                          Sichtbar
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="flex items-center gap-1 w-fit">
                          <EyeOff className="w-3 h-3" />
                          Ausgeblendet
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {flag.sichtbar_fuer_rollen.slice(0, 2).map((rolle) => (
                          <Badge key={rolle} variant="outline" className="text-xs">
                            {rolle}
                          </Badge>
                        ))}
                        {flag.sichtbar_fuer_rollen.length > 2 && (
                          <Badge variant="outline" className="text-xs">
                            +{flag.sichtbar_fuer_rollen.length - 2}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Switch
                          checked={flag.ist_aktiv}
                          onCheckedChange={() => handleToggle(flag.menu_key)}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {flags.length > 0 && (
            <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <div className="flex gap-2">
                <Shield className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-900 dark:text-amber-200 mb-1">
                    Hinweis zur Menüpunkt-Verwaltung
                  </p>
                  <ul className="text-amber-800 dark:text-amber-300 space-y-1">
                    <li>• Ausgeblendete Menüpunkte sind für normale User nicht sichtbar</li>
                    <li>• Code und Routes bleiben vorhanden (für Tests via URL)</li>
                    <li>• Ideal für Features in Entwicklung (z.B. Atemschutz)</li>
                    <li>• Änderungen werden sofort wirksam</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Initialize Dialog */}
      <Dialog open={showInitDialog} onOpenChange={setShowInitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Standard Feature Flags initialisieren?</DialogTitle>
            <DialogDescription>
              Dies erstellt Feature Flags für alle Standard-Menüpunkte. 
              Atemschutz wird standardmäßig ausgeblendet.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInitDialog(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleInitialize}>
              Initialisieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
